import { type LazyExoticComponent, type ReactNode, createElement, lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { type RouteNamesKey, RouteNames } from './routesNames';
import Loader from '@/components/Loader/Loader';

export interface AppRoute {
  name: RouteNamesKey;
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  element?: LazyExoticComponent<React.FC<any>>;
  protected?: boolean;
  roles?: string[];
  children?: AppRoute[];
  menu?: {
    title?: string;
    icon?: ReactNode;
    hideInMenu?: boolean;
  };
}

const routesConfig: AppRoute[] = [
  {
    name: RouteNames.HOME,
    path: '/',
    element: lazy(() => import('@/pages/Home')),
    protected: true,
    menu: { title: 'Home', icon: <HomeRoundedIcon /> },
  },
  {
    name: RouteNames.CONTACT_US,
    path: '/contact-us',
    element: lazy(() => import('@/pages/ContactUs/ContactUs')),
    menu: { title: 'Contact Us', hideInMenu: true },
  },
  {
    name: RouteNames.POLLING_LOCATOR,
    path: '/polling-locator',
    element: lazy(() => import('@/pages/PollingLocator/PollingLocator')),
    menu: { title: 'Polling Locator' },
  },
  {
    name: RouteNames.MENU_DETAILS,
    path: '/menu-details/:parentId/:childId/:categoryId?',
    element: lazy(() => import('@/pages/MenuDetails/MenuDetails')),
    menu: { title: 'Menu Details' },
  },
  {
    name: RouteNames.SEARCH_RESULTS,
    path: '/search-results',
    element: lazy(() => import('@/pages/SearchResults/SearchResults')),
    menu: { title: 'Search Results' },
  },
  {
    name: RouteNames.CONTENT_DETAILS,
    path: '/content-details/:id',
    element: lazy(() => import('@/pages/ContentDetails/ContentDetails')),
    menu: { title: 'Content Details' },
  },
  {
    name: RouteNames.STATISTICS,
    path: '/statistics',
    element: lazy(() => import('@/pages/Statistics/Statistics')),
    menu: { title: 'Statistics' },
  },
];

/**
 * Helper function to recursively find the full path of a route by its name.
 * Handles concatenation of parent and child paths.
 *
 * @param currentRoutes The array of route configurations to search within.
 * @param targetName The unique name of the route to find.
 * @param parentPath The accumulated path from parent routes (defaults to '').
 * @returns The full constructed path, or undefined if not found.
 */
function findRouteRecursive(
  currentRoutes: AppRoute[],
  targetName: RouteNamesKey,
  parentPath: string = '',
): string | undefined {
  for (const route of currentRoutes) {
    // Clean up parentPath and current route.path for consistent joining
    const cleanedParentPath =
      parentPath.endsWith('/') && parentPath !== '/'
        ? parentPath.slice(0, -1) // Remove trailing slash if not root
        : parentPath;
    const cleanedRoutePath = route.path.startsWith('/')
      ? route.path.slice(1) // Remove leading slash if present
      : route.path;

    // Construct the combined path
    let currentCombinedPath = cleanedParentPath;
    if (cleanedRoutePath !== '') {
      // Only append if child path is not empty
      currentCombinedPath += `/${cleanedRoutePath}`;
    }

    // Normalize the path: ensure leading slash, no double slashes, and no trailing slash (unless it's just '/')
    let normalizedPath = currentCombinedPath.replace(/\/\//g, '/'); // Replace any double slashes
    if (!normalizedPath.startsWith('/')) {
      // Ensure it starts with a single slash
      normalizedPath = '/' + normalizedPath;
    }
    normalizedPath = normalizedPath === '/' ? '/' : normalizedPath.replace(/\/$/, ''); // Remove trailing slash unless it's root

    // If the current route's name matches the target name, return its full path
    if (route.name === targetName) {
      return normalizedPath;
    }

    // If the current route has children, recursively search within them
    if (route.children && route.children.length > 0) {
      const foundPathInChild = findRouteRecursive(
        route.children,
        targetName,
        normalizedPath, // Pass the newly constructed and normalized path as the new parentPath
      );
      if (foundPathInChild) {
        return foundPathInChild; // If found in children, bubble it up
      }
    }
  }
  return undefined; // Route not found in this branch
}

/**
 * Simple getRoutePath helper function to construct a URL path based on route name and parameters.
 * Supports nested routes and dynamic segments.
 *
 * @param name The unique name of the route (from RouteNamesKey).
 * @param params Optional object for dynamic path segments (e.g., { id: '123' }).
 * @returns The constructed URL path, or '/' if the route is not found.
 */
export function getRoutePath(
  name: RouteNamesKey,
  params?: Record<string, string | number>, // For dynamic segments like :id
): string {
  // Use the recursive helper to find the base path for the given route name
  let path = findRouteRecursive(routesConfig, name);

  if (!path) {
    // eslint-disable-next-line no-console
    console.error(`Error: Route name "${name}" not found in routes configuration.`);
    return '/'; // Fallback to root or consider throwing an error for strictness
  }

  // Replace dynamic segments (e.g., :id, :settingId) if parameters are provided
  if (params) {
    for (const key in params) {
      // Ensure the property is directly on the params object, not from prototype chain
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        // Use a global regex to replace all occurrences of the dynamic segment
        path = path.replace(new RegExp(`:${key}`, 'g'), String(params[key]));
      }
    }
  }

  // Basic runtime check for any unreplaced dynamic segments (useful for debugging)
  // This helps catch cases where required params were not provided.
  if (path.includes(':')) {
    // eslint-disable-next-line no-console
    console.warn(
      `Warning: Route "${name}" has unreplaced dynamic segments. Missing parameters? Path: ${path}`,
    );
  }

  return path;
}

export const generateRoutes = (routes: AppRoute[]): RouteObject[] => {
  return routes.map((route) => {
    let element;
    if (route.element) {
      element = <Suspense fallback={<Loader />}>{createElement(route.element)}</Suspense>;
    }
    return {
      path: route.path,
      element: element,
      children: route.children ? generateRoutes(route.children) : undefined,
    };
  });
};

export default routesConfig;
